import React, { useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Input,
  Text,
  VStack,
  DrawerRoot,
  DrawerBackdrop,
  DrawerPositioner,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerCloseTrigger,
} from '@chakra-ui/react';
import { ITableFilters } from '../hooks/useCarichiTable';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';

interface FiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: ITableFilters;
  onApply: (f: ITableFilters) => void;
  rosterAthletes: { _id: string; firstName: string; lastName: string; roles?: string[] }[];
  customGroups: { _id: string; name: string }[];
}

const SESSION_TYPES = [
  { id: 'training', label: 'Allenamento' },
  { id: 'match', label: 'Partita' },
  { id: 'friendly', label: 'Amichevole' },
  { id: 'test', label: 'Test' },
  { id: 'individual', label: 'Individuale' },
  { id: 'rehab', label: 'Rehab' },
];

const INTENSITY_BUCKETS = [
  { id: 'low', label: 'Bassa (sRPE < 300)' },
  { id: 'medium', label: 'Media (300-550)' },
  { id: 'high', label: 'Alta (> 550)' },
];

const PERCENTILE_OPTIONS = [
  { id: 'lt_p25', label: '< P25 (sotto la norma)' },
  { id: 'p25_p75', label: 'P25-P75 (norma)' },
  { id: 'gt_p75', label: '> P75 (sopra la norma)' },
  { id: 'outlier', label: 'Solo outlier (<P10 o >P90)' },
];

const BASELINE_OPTIONS = [
  { id: 'above', label: 'Sopra baseline (> +10%)' },
  { id: 'in_line', label: 'In linea (±10%)' },
  { id: 'below', label: 'Sotto baseline (< -10%)' },
];

const ACTIVITY_CATEGORIES = [
  { id: 'warmup', label: 'Riscaldamento' },
  { id: 'technical', label: 'Tecnica' },
  { id: 'tactical', label: 'Tattica' },
  { id: 'match_play', label: 'Partitella' },
  { id: 'conditioning', label: 'Condizionamento' },
  { id: 'strength', label: 'Forza' },
  { id: 'recovery', label: 'Recupero' },
  { id: 'cooldown', label: 'Defaticamento' },
];

/**
 * Drawer Filtri 9 sezioni (Addendum v2 Parte 18 + v3 Parte 26).
 * 4 essenziali (sempre visibili): Ruolo, Gruppo, Tipo sessione, Stato atleta
 * 5 avanzati (collassabili): Intensità, Zona RPE, Percentile, Baseline, Categoria activity
 */
export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  open,
  onClose,
  filters,
  onApply,
  rosterAthletes,
  customGroups,
}) => {
  const [local, setLocal] = useState<ITableFilters>(filters);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (open) setLocal(filters);
  }, [open, filters]);

  // Estrai ruoli unici dal roster
  const allRoles = Array.from(
    new Set(rosterAthletes.flatMap((a) => a.roles ?? [])),
  ).sort();

  const toggleArrayValue = (
    arr: string[] | undefined,
    val: string,
  ): string[] => {
    const cur = arr ?? [];
    return cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
  };

  const activeCount =
    (local.roles?.length ?? 0) +
    (local.sessionTypes?.length ?? 0) +
    (local.intensityBuckets?.length ?? 0) +
    (local.activityCategoryFilter?.length ?? 0) +
    (local.baselineFilter?.length ?? 0) +
    (local.customGroupId ? 1 : 0) +
    (local.percentileFilter ? 1 : 0) +
    (local.rpeRange &&
    (local.rpeRange.min !== 0 || local.rpeRange.max !== 10)
      ? 1
      : 0);

  // Opzioni per CustomSelectInput gruppo personalizzato
  const groupOptions = [
    { value: '', label: '— nessuno —' },
    ...customGroups.map((g) => ({ value: g._id, label: g.name })),
  ];

  // Opzioni per CustomSelectInput percentile
  const percentileOptions = [
    { value: '', label: '— nessun filtro —' },
    ...PERCENTILE_OPTIONS.map((o) => ({ value: o.id, label: o.label })),
  ];

  return (
    <DrawerRoot
      open={open}
      onOpenChange={(e) => { if (!e.open) onClose(); }}
      placement="end"
      size="md"
    >
      <DrawerBackdrop />
      <DrawerPositioner>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              Filtri
              <Text as="span" fontSize="xs" color="gray.500" fontWeight={400} ml={2}>
                {activeCount} attivi
              </Text>
            </DrawerTitle>
            <DrawerCloseTrigger />
          </DrawerHeader>

          <DrawerBody>
            <VStack gap={4} alignItems="stretch">
              {/* 1. Ruolo */}
              <Box>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                  Ruolo ({local.roles?.length ?? 0} sel.)
                </Text>
                <HStack flexWrap="wrap" gap={1}>
                  {allRoles.length === 0 && (
                    <Text fontSize="xs" color="gray.400">Nessun ruolo nel roster</Text>
                  )}
                  {allRoles.map((r) => {
                    const sel = local.roles?.includes(r) ?? false;
                    return (
                      <button
                        key={r}
                        className={`leo-chip ${sel ? 'is-active' : ''}`}
                        onClick={() => setLocal({ ...local, roles: toggleArrayValue(local.roles, r) })}
                      >
                        {r}
                      </button>
                    );
                  })}
                </HStack>
              </Box>

              {/* 2. Gruppo personalizzato */}
              <Box>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                  Gruppo personalizzato
                </Text>
                <CustomSelectInput
                  options={groupOptions}
                  value={local.customGroupId ?? ''}
                  onChange={(val: string) => setLocal({ ...local, customGroupId: val || undefined })}
                  placeholder="— nessuno —"
                  menuPosition="fixed"
                />
              </Box>

              {/* 3. Tipo sessione */}
              <Box>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                  Tipo sessione ({local.sessionTypes?.length ?? 0} sel.)
                </Text>
                <HStack flexWrap="wrap" gap={1}>
                  {SESSION_TYPES.map((t) => {
                    const sel = local.sessionTypes?.includes(t.id) ?? false;
                    return (
                      <button
                        key={t.id}
                        className={`leo-chip ${sel ? 'is-active' : ''}`}
                        onClick={() => setLocal({ ...local, sessionTypes: toggleArrayValue(local.sessionTypes, t.id) })}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </HStack>
              </Box>

              {/* 4. Stato atleta — placeholder per future estensioni */}
              <Box>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                  Stato atleta
                </Text>
                <Text fontSize="xs" color="gray.400" fontStyle="italic">
                  Default: Disponibili. (Filtro stato Infortunato/Permesso/Squalificato — sprint futuro)
                </Text>
              </Box>

              {/* Toggle avanzati */}
              <CustomButton
                color="text"
                onClick={() => setAdvancedOpen(!advancedOpen)}
              >
                {advancedOpen ? '▾' : '▸'} Filtri avanzati
              </CustomButton>

              {advancedOpen && (
                <>
                  {/* 5. Intensità sessione */}
                  <Box>
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                      Intensità sessione (sRPE)
                    </Text>
                    <HStack flexWrap="wrap" gap={1}>
                      {INTENSITY_BUCKETS.map((b) => {
                        const sel = local.intensityBuckets?.includes(b.id) ?? false;
                        return (
                          <button
                            key={b.id}
                            className={`leo-chip ${sel ? 'is-active' : ''}`}
                            onClick={() => setLocal({ ...local, intensityBuckets: toggleArrayValue(local.intensityBuckets, b.id) })}
                          >
                            {b.label}
                          </button>
                        );
                      })}
                    </HStack>
                  </Box>

                  {/* 6. Zona RPE range */}
                  <Box>
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                      Zona RPE: {local.rpeRange?.min ?? 0} - {local.rpeRange?.max ?? 10}
                    </Text>
                    <HStack gap={2}>
                      <Input
                        type="number"
                        size="sm"
                        min={0}
                        max={10}
                        step={0.5}
                        value={local.rpeRange?.min ?? 0}
                        onChange={(e) => setLocal({
                          ...local,
                          rpeRange: { min: parseFloat(e.target.value), max: local.rpeRange?.max ?? 10 },
                        })}
                      />
                      <Text>—</Text>
                      <Input
                        type="number"
                        size="sm"
                        min={0}
                        max={10}
                        step={0.5}
                        value={local.rpeRange?.max ?? 10}
                        onChange={(e) => setLocal({
                          ...local,
                          rpeRange: { min: local.rpeRange?.min ?? 0, max: parseFloat(e.target.value) },
                        })}
                      />
                    </HStack>
                  </Box>

                  {/* 7. Percentile squadra */}
                  <Box>
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                      Percentile squadra
                    </Text>
                    <CustomSelectInput
                      options={percentileOptions}
                      value={local.percentileFilter ?? ''}
                      onChange={(val: string) => setLocal({ ...local, percentileFilter: val || undefined })}
                      placeholder="— nessun filtro —"
                      menuPosition="fixed"
                    />
                  </Box>

                  {/* 8. vs Baseline personale */}
                  <Box>
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                      vs Baseline personale (rolling 28d)
                    </Text>
                    <HStack flexWrap="wrap" gap={1}>
                      {BASELINE_OPTIONS.map((b) => {
                        const sel = local.baselineFilter?.includes(b.id) ?? false;
                        return (
                          <button
                            key={b.id}
                            className={`leo-chip ${sel ? 'is-active' : ''}`}
                            onClick={() => setLocal({ ...local, baselineFilter: toggleArrayValue(local.baselineFilter, b.id) })}
                          >
                            {b.label}
                          </button>
                        );
                      })}
                    </HStack>
                  </Box>

                  {/* 9. Categoria attività */}
                  <Box>
                    <Text fontSize="xs" fontWeight="700" textTransform="uppercase" mb={2} color="gray.600">
                      Categoria attività ({local.activityCategoryFilter?.length ?? 0})
                    </Text>
                    <HStack flexWrap="wrap" gap={1}>
                      {ACTIVITY_CATEGORIES.map((c) => {
                        const sel = local.activityCategoryFilter?.includes(c.id) ?? false;
                        return (
                          <button
                            key={c.id}
                            className={`leo-chip ${sel ? 'is-active' : ''}`}
                            onClick={() => setLocal({ ...local, activityCategoryFilter: toggleArrayValue(local.activityCategoryFilter, c.id) })}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </HStack>
                  </Box>
                </>
              )}
            </VStack>
          </DrawerBody>

          <DrawerFooter>
            <CustomButton color="error" onClick={() => setLocal({})}>
              Azzera tutti
            </CustomButton>
            <HStack gap={2}>
              <CustomButton color="text" onClick={onClose}>
                Annulla
              </CustomButton>
              <CustomButton color="primary" onClick={() => { onApply(local); onClose(); }}>
                Applica ({activeCount})
              </CustomButton>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </DrawerPositioner>
    </DrawerRoot>
  );
};
